import { useEffect } from "react";

const BACKEND_URL = (typeof window !== "undefined" && window.location.origin) || "";

const upsertMeta = (selector, attrName, attrVal, content) => {
    if (typeof document === "undefined") return;
    let el = document.head.querySelector(selector);
    if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attrName, attrVal);
        document.head.appendChild(el);
    }
    el.setAttribute("content", content || "");
};

const upsertLink = (rel, href, attrs = {}) => {
    if (typeof document === "undefined") return;
    let el = document.head.querySelector(`link[rel="${rel}"]${attrs.hreflang ? `[hreflang="${attrs.hreflang}"]` : ""}`);
    if (!el) {
        el = document.createElement("link");
        el.rel = rel;
        if (attrs.hreflang) el.setAttribute("hreflang", attrs.hreflang);
        document.head.appendChild(el);
    }
    el.href = href;
};

const upsertJsonLd = (id, payload) => {
    if (typeof document === "undefined") return;
    let el = document.head.querySelector(`script[data-jsonld="${id}"]`);
    if (!el) {
        el = document.createElement("script");
        el.type = "application/ld+json";
        el.dataset.jsonld = id;
        document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(payload);
};

export const useSeo = ({
    title,
    description,
    path = "/",
    ogImage,
    type = "website",
    structuredData = null,
    locale = "tr",
}) => {
    useEffect(() => {
        if (typeof document === "undefined") return;
        const fullTitle = title
            ? `${title} | Otel Pro`
            : "Otel Pro — Türkiye'nin Otel Endüstrisi Platformu";
        document.title = fullTitle;

        const cleanPath = path.replace(/\/$/, "") || "/";
        const fullUrl = `${BACKEND_URL}${cleanPath === "/" ? "" : cleanPath}`;

        upsertMeta('meta[name="description"]', "name", "description", description || "");
        upsertMeta('meta[property="og:title"]', "property", "og:title", fullTitle);
        upsertMeta('meta[property="og:description"]', "property", "og:description", description || "");
        upsertMeta('meta[property="og:type"]', "property", "og:type", type);
        upsertMeta('meta[property="og:url"]', "property", "og:url", fullUrl);
        if (ogImage) {
            upsertMeta('meta[property="og:image"]', "property", "og:image", ogImage);
        }
        upsertMeta('meta[name="twitter:card"]', "name", "twitter:card", "summary_large_image");
        upsertMeta('meta[name="twitter:title"]', "name", "twitter:title", fullTitle);
        upsertMeta('meta[name="twitter:description"]', "name", "twitter:description", description || "");

        upsertLink("canonical", fullUrl);
        upsertLink("alternate", `${BACKEND_URL}${cleanPath === "/" ? "" : cleanPath}`, { hreflang: "x-default" });
        upsertLink("alternate", `${BACKEND_URL}${cleanPath === "/" ? "" : cleanPath}`, { hreflang: "tr" });
        upsertLink("alternate", `${BACKEND_URL}/en${cleanPath === "/" ? "" : cleanPath}`, { hreflang: "en" });
        upsertLink("alternate", `${BACKEND_URL}/de${cleanPath === "/" ? "" : cleanPath}`, { hreflang: "de" });

        // Organization JSON-LD (without sameAs to other ecosystem sites per rules)
        upsertJsonLd("organization", {
            "@context": "https://schema.org",
            "@type": "Organization",
            name: "Otel Pro",
            url: BACKEND_URL,
            logo: "https://customer-assets.emergentagent.com/job_otel-central/artifacts/tvucn0yf_wordmark-light.svg",
            description: "Türkiye'nin Otel Endüstrisi Platformu",
        });

        if (structuredData) {
            upsertJsonLd(structuredData["@type"] || "page", structuredData);
        }
    }, [title, description, path, ogImage, type, structuredData, locale]);
};

export default useSeo;
